<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Organization Activity Report"
                :showAddNew="false"
            />
        </div>
        <div class="my-2 px-2">
          <DateQuerySetter
              btnTitle="Go"
              @onClickGo="onClickToGo"
              class="tds-vds-query-setter"
          >
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
              <label for="colFormLabel" class="col-form-label">Team Member</label>
              <AsyncSelect
                  placeholder="Team Member"
                  v-model="contactProfile"
                  :api-service="fetchContactProfiles"
                  :additional-query="{roles: 'employee', status: 'active'}"
                  label="name"
              />
            </div>

            <div class="col-12 col-sm-6 col-md-6 col-lg-2">
                    <label for="colFormLabel" class="col-form-label">Customer</label>
                    <AsyncSelect
                        placeholder="Select Customer"
                        v-model="client"
                        :api-service="fetchContactProfiles"
                    />
                </div>
          </DateQuerySetter>
        </div>

      <div class="col-12">
        <div class="d-flex justify-content-end px-2">
          <button
            @click="goToPrint"
            class="btn btn-primary btn-sm btn-download-invoice waves-effect me-1"
          >
            Print
          </button>

          <button
            @click="exportTable"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
          >
            Export
          </button>
        </div>
      </div>

      <div class="table-responsive mt-2 mb-2 px-1">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Activity</th>
              <th class="text-end">Total Hours</th>
              <th class="text-end">Approved</th>
              <th class="text-end">Non Approved Hours</th>
              <th class="text-end">Total Cost</th>
              <th class="text-end">Approved Cost</th>
              <th class="text-end">Non Approved Cost</th>
            </tr>
          </thead>
        
        <tbody>
          <tr v-for="row in flatRows" :key="row.id">
            <td class="px-1">
              <span
                :style="{ paddingLeft: `${row.level * 20}px` }"
                :class="row.type === 'group' ? 'text-bold' : ''"
              >
                {{ row.name }}
              </span>
            </td>
          
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.total_hours) }}</td>
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.approved_hours) }}</td>
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.completed_hours) }}</td>
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.total_cost) }}</td>
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.approved_cost) }}</td>
            <td class="text-end" :class="row.type === 'group' ? 'text-bold' : ''">{{ commaFormat(row.completed_cost) }}</td>
          </tr>
          <tr v-if="totalRow" class="text-bold">
            <td class="px-1 text-end">{{ totalRow.name }}</td>
            <td class="text-end">{{ commaFormat(totalRow.total_hours) }}</td>
            <td class="text-end">{{ commaFormat(totalRow.approved_hours) }}</td>
            <td class="text-end">{{ commaFormat(totalRow.completed_hours) }}</td>
            <td class="text-end">{{ commaFormat(totalRow.total_cost) }}</td>
            <td class="text-end">{{ commaFormat(totalRow.approved_cost) }}</td>
            <td class="text-end">{{ commaFormat(totalRow.completed_cost) }}</td>
          </tr>
        </tbody>
        </table>
      </div>

     <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleActivity from "@/services/modules/procurement/activity";
import handleContact from '@/services/modules/contact'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import pdfPrinter from '@/services/utils/pdf/organizationActivityReport';
import handleCompany from "@/services/modules/company";
import { commaFormat } from '@/services/utils/global';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const client = ref(null);
const contactProfile = ref(null);
const loading = ref(false)
const tableItems = ref([]);
const token = computed(() => localStorage.getItem('token'));

const flatRows = computed(() => {
  if (!Array.isArray(tableItems.value)) return []

  const rows = []
  flattenTree(tableItems.value, 0, rows)
  return rows
})

const totalRow = computed(() => {
  const topLevelGroups = flatRows.value.filter(row => row.level === 0 && row.type === 'group')
  
  if (topLevelGroups.length === 0) return null

  return {
    id: 'total-row',
    name: 'Total',
    type: 'total',
    level: 0,
    total_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.total_hours) || 0), 0),
    approved_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.approved_hours) || 0), 0),
    completed_hours: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.completed_hours) || 0), 0),
    total_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.total_cost) || 0), 0),
    approved_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.approved_cost) || 0), 0),
    completed_cost: topLevelGroups.reduce((sum, row) => sum + (parseFloat(row.completed_cost) || 0), 0)
  }
})

const { fetchOrganizationActivityReport } = handleActivity()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany();
const { exportToPDF } = pdfPrinter();

const onClickToGo = () => {
  getOrganizationActivityReportList();
}

const goToPrint = async () => {
  const response =  await fetchCompanyInfo(route.params.companyId);
  exportToPDF(response.data, flatRows.value, { start: route.query.start, end: route.query.end });
}

const exportTable = () => {
  let query = getQuery();
  let url = `${process.env.VUE_APP_BASE_URL}/api/procurement/activity-manager/organization-activity-report${query}&_token=${token.value}&is_export=1&is_all=1`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

function flattenTree(nodes, level = 0, rows = []) {
  nodes.forEach(node => {
    rows.push({
      id: node.id,
      name: node.name,
      type: node.type,
      level,
      total_hours: node.total_hours,
      approved_hours: node.approved_hours,
      completed_hours: node.completed_hours,
      total_cost: node.total_cost,
      approved_cost: node.approved_cost,
      completed_cost: node.completed_cost
    })

    if (node.child_recursive && node.child_recursive.length) {
      flattenTree(node.child_recursive, level + 1, rows)
    }
  })

  return rows
}

const getOrganizationActivityReportList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchOrganizationActivityReport(query).then(res => {
      if (!res.status){
        return
      }
      tableItems.value = res.data
    }).catch(err => {
      console.log(err)
    }).finally(() => {
      loading.value = false
    })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (route.query.start) query += '&start_date=' + route.query.start;
  if (route.query.end) query += '&end_date=' + route.query.end;
  if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`;
  if(client.value) query += `&client_id=${client.value.id}`;

  return query;
}

onMounted(() => {
  Promise.all([
    getOrganizationActivityReportList(),
  ])
})

</script>
